import { type FormEvent, useState } from 'react';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';
import { Dialog } from './Dialog.js';

const { t } = i18n;

// Roles a partner user can hold. The invitation is always scoped to THIS
// partner (scopeType PARTNER, scopeId = partner.id) — that scope, not the
// email, is what links the accepted user to the partner (Jonas #3, 2026-06-18).
const PARTNER_ROLES = ['PARTNER_ADMIN', 'ORDER_PLACER'] as const;
type PartnerRole = (typeof PARTNER_ROLES)[number];

export function InvitePartnerUserDialog({
  partnerId,
  partnerName,
  defaultEmail,
  defaultName,
  onClose,
  onSent,
}: {
  partnerId: string;
  partnerName: string;
  defaultEmail: string | null;
  defaultName: string | null;
  onClose: () => void;
  onSent: () => void;
}) {
  const [email, setEmail] = useState(defaultEmail ?? '');
  const [name, setName] = useState(defaultName ?? '');
  const [role, setRole] = useState<PartnerRole>('PARTNER_ADMIN');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [emailWarning, setEmailWarning] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await api<{ emailDelivered?: boolean; emailError?: string | null }>(
        '/invitations',
        {
          method: 'POST',
          body: {
            email: email.trim(),
            displayName: name.trim(),
            role,
            scopeType: 'PARTNER',
            scopeId: partnerId,
          },
        },
      );
      // The invitation is persisted even when the email fails to send — warn
      // rather than claim success, so the admin knows to re-send or share the
      // link manually.
      if (res?.emailDelivered === false) {
        setEmailWarning(
          t('partner.invite.email_failed').replace('{{reason}}', res.emailError ?? ''),
        );
      }
      setSent(true);
      onSent();
    } catch {
      setError(t('common.error'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={t('partner.invite.title')} onClose={onClose}>
      {sent ? (
        <div className="space-y-3 text-sm">
          {emailWarning ? (
            <p className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-100">
              {emailWarning}
            </p>
          ) : (
            <p>{t('partner.invite.sent')}</p>
          )}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded bg-primary text-primary-foreground px-3 py-1.5"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={(e) => void onSubmit(e)} className="space-y-3 text-sm">
          <p className="text-xs text-muted-foreground">
            {t('partner.invite.hint').replace('{{name}}', partnerName)}
          </p>
          <label className="block space-y-1">
            <span className="font-medium">{t('auth.login.email')}</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full rounded border border-border bg-background px-2 py-1.5"
            />
          </label>
          <label className="block space-y-1">
            <span className="font-medium">{t('invitation.display_name')}</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full rounded border border-border bg-background px-2 py-1.5"
            />
          </label>
          <label className="block space-y-1">
            <span className="font-medium">{t('invitations.role')}</span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as PartnerRole)}
              className="w-full rounded border border-border bg-background px-2 py-1.5"
            >
              {PARTNER_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>

          {error ? <div className="text-red-600">{error}</div> : null}

          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <button type="button" onClick={onClose} className="rounded border border-border px-3 py-1.5">
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={busy || !email.trim() || !name.trim()}
              className="rounded bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
            >
              {busy ? t('common.loading') : t('invitations.send')}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
